import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { FilterType } from '~/components/shared'
import { HealthProcedureVariantProps } from '~/constants'
import { useFetchHealthProcedures } from '~/hooks'
import {
  createTypeFiltersTagWith,
  createDateFilterTagWith,
  createSelectedPeriodFromPrevious,
  EMedicalViews,
  IFetchHealthProceduresParams,
  IFilterTag,
  IHealthProcedures,
  TimelineGeneralPeriodType,
  ViewsType,
  EHealthProceduresTypes,
  createSearchTermTagWith
} from '~/interfaces'
import { removeUnsupportedEvents } from '~/services'
import { identifyInProgressEvents, transformDate } from '~/utils'

interface GeneralHealthProceduresProviderProps {
  children: React.ReactNode
}

const defaultValue = {
  mainContentType: 'humanBody' as ViewsType,
  healthProcedures: [] as IHealthProcedures[],
  filterTags: [] as IFilterTag[],
  selectedPeriod: { period: 'years', previousPeriod: 'none' } as ISelectedPeriod
}

export interface IGeneralHealthProceduresContextHelpers {
  isLoadingTimeline: boolean
  errorTimeline: string
}

interface ISelectedPeriod {
  period: TimelineGeneralPeriodType
  previousPeriod: TimelineGeneralPeriodType
}

export interface IGeneralHealthProceduresContextProps extends IGeneralHealthProceduresContextHelpers {
  displayedHealthProcedures: IHealthProcedures[]
  getHealthProcedures: (params: IFetchHealthProceduresParams) => void

  selectedPeriod: ISelectedPeriod
  updateSelectedPeriod: (newSelectedPeriod: ISelectedPeriod) => void

  mainContentType: ViewsType
  changeMainContentType: (newContentType: ViewsType) => void

  modalHealthProcedure: IHealthProcedures | null
  selectMedicalEventForDetailing: (healthProcedures: IHealthProcedures | null) => void
  handleFilterHealthProceduresPerType: (types: FilterType[]) => void
  handleFilterHealthProceduresPerDate: (rangeDates: Date[]) => void
  initializeSidePanel: () => void
  dateFiltersActive: Date[]
  typeFiltersActive: string[]

  openConductCardList: boolean
  setOpenConductCardList: Dispatch<SetStateAction<boolean>>
  openOmicsCardList: boolean
  setOpenOmicsCardList: Dispatch<SetStateAction<boolean>>

  showHealthProcedureDetails: () => boolean
  closeHealthProcedureDetails: () => void

  hasDateFiltersActive: boolean
  hasTypeFiltersActive: boolean

  filterTags: IFilterTag[]
  setFilterTagToAdmissionEvent: (newTag: IFilterTag) => void
  removeFilterTag: (tag: IFilterTag) => void
  resetFilterTags: () => void

  filterHealthProceduresByTerm: (term: IFilterTag) => void

  sidePanelEvents: IHealthProcedures[]
  updateSidePanelEvents: (events: IHealthProcedures[]) => void
  resetSidePanelEvents: () => void
}

const GeneralHealthProceduresContext = React.createContext<IGeneralHealthProceduresContextProps>(
  {} as IGeneralHealthProceduresContextProps
)

function GeneralHealthProceduresProvider({ children }: GeneralHealthProceduresProviderProps): JSX.Element {
  const [mainContentType, setMainContentType] = useState<ViewsType>(defaultValue.mainContentType)
  const [selectedPeriod, setSelectedPeriod] = useState<ISelectedPeriod>(defaultValue.selectedPeriod)

  const [modalHealthProcedure, setModalHealthProcedure] = useState<IHealthProcedures | null>(null)
  const [dateFiltersActive, setDateFiltersActive] = useState<Date[]>([])
  const [typeFiltersActive, setTypeFiltersActive] = useState<string[]>([])
  const [filterTags, setFilterTags] = useState<IFilterTag[]>(defaultValue.filterTags)

  const [openConductCardList, setOpenConductCardList] = useState<boolean>(false)
  const [openOmicsCardList, setOpenOmicsCardList] = useState<boolean>(false)

  const [healthProceduresParams, setHealthProceduresParams] = useState<IFetchHealthProceduresParams | null>(null)
  const [displayedHealthProcedures, setDisplayedHealthProcedures] = useState<IHealthProcedures[]>(
    defaultValue.healthProcedures
  )
  const {
    data: healthProcedures,
    isLoading: isLoadingTimeline,
    error: errorTimeline
  } = useFetchHealthProcedures(healthProceduresParams, EMedicalViews.GENERAL)

  const [sidePanelEvents, setSidePanelEvents] = useState<IHealthProcedures[]>([])
  const updateSidePanelEvents = useCallback((events: IHealthProcedures[]) => setSidePanelEvents(events), [])
  const resetSidePanelEvents = useCallback(() => {
    if (healthProcedures?.length) updateSidePanelEvents(healthProcedures)
  }, [updateSidePanelEvents, healthProcedures])

  const treatFetchedHealthProcedures = useCallback((healthProcedures: IHealthProcedures[]) => {
    const validEvents = removeUnsupportedEvents(healthProcedures)
    const inProgressEvents = identifyInProgressEvents(validEvents)
    return inProgressEvents
  }, [])

  useEffect(() => {
    if (healthProcedures) {
      setDisplayedHealthProcedures(treatFetchedHealthProcedures(healthProcedures))
    }
  }, [healthProcedures, treatFetchedHealthProcedures])

  const getHealthProcedures = useCallback((params: IFetchHealthProceduresParams): void => {
    setHealthProceduresParams(params)
  }, [])

  const initializeSidePanel = useCallback(() => {
    resetSidePanelEvents()
  }, [resetSidePanelEvents])

  useEffect(() => {
    if (displayedHealthProcedures.length > 0) {
      initializeSidePanel()
    }
  }, [displayedHealthProcedures, initializeSidePanel])

  const changeMainContentType = useCallback((newMainContentType: ViewsType) => {
    setMainContentType(newMainContentType)
  }, [])

  const updateSelectedPeriod = (newSelectedPeriod: ISelectedPeriod): void => {
    changeMainContentType('timeline')
    setSelectedPeriod({ ...newSelectedPeriod })
  }

  const selectMedicalEventForDetailing = (healthProcedures: IHealthProcedures | null) => {
    setModalHealthProcedure(healthProcedures)
  }

  const updateTypeFilterTags = useCallback((filters: FilterType[]): void => {
    const tagsOfTypeFilters = filters.map(filter => {
      const filterColor = HealthProcedureVariantProps[filter.type[0]].color
      return createTypeFiltersTagWith(filter.text, filterColor, filter.type)
    })

    setFilterTags(existingTags => {
      const existentTags = existingTags.filter(tag => tag.type !== 'typeFilter')
      return [...existentTags, ...tagsOfTypeFilters]
    })
  }, [])

  const handleFilterHealthProceduresPerType = useCallback(
    (typeFilters: FilterType[]) => {
      const activeTypes = typeFilters.flatMap(filter => filter.type)
      setTypeFiltersActive(activeTypes)
      updateTypeFilterTags(typeFilters)
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
      changeMainContentType('timeline')
    },
    [updateTypeFilterTags, initializeSidePanel, changeMainContentType]
  )

  const removeTypeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      const newTypeFiltersActive = typeFiltersActive.filter(type => {
        const tagTypes = tag.id.split('-')
        return !tagTypes.includes(type)
      })
      setTypeFiltersActive(newTypeFiltersActive)
    },
    [typeFiltersActive]
  )

  const hasTypeFiltersActive: boolean = typeFiltersActive.length > 0

  const updateDateFilterTags = useCallback(
    (rangeDates: Date[]): void => {
      const tagsWithoutDateFilter = filterTags.filter(tag => tag.type !== 'dateFilter')
      const isNotAValidRangeOfDates = rangeDates.length <= 1
      if (isNotAValidRangeOfDates) {
        setFilterTags(tagsWithoutDateFilter)
        return
      }

      const formattedDates = rangeDates.map(date => transformDate({ date }))
      const tagDescription = formattedDates.join(' - ')
      setFilterTags([...tagsWithoutDateFilter, createDateFilterTagWith(tagDescription)])
    },
    [filterTags]
  )

  const handleFilterHealthProceduresPerDate = useCallback(
    (rangeDates: Date[]) => {
      const isAValidRangeOfDates = rangeDates.length !== 1

      if (isAValidRangeOfDates) {
        setDateFiltersActive(rangeDates)
        updateDateFilterTags(rangeDates)
        initializeSidePanel()
        setOpenOmicsCardList(true)
        setOpenConductCardList(true)
        changeMainContentType('timeline')
      }
    },
    [changeMainContentType, initializeSidePanel, updateDateFilterTags]
  )

  const removeDateFilterTag = (): void => {
    setDateFiltersActive([])
  }

  const hasDateFiltersActive: boolean = dateFiltersActive.length > 0

  const showHealthProcedureDetails = (): boolean => {
    return !!modalHealthProcedure
  }

  const closeHealthProcedureDetails = (): void => {
    setModalHealthProcedure(null)
  }

  const setFilterTagToAdmissionEvent = (newTag: IFilterTag): void => {
    const tagsWithoutAdmissionFilterTag = filterTags.filter(tag => tag.id !== EHealthProceduresTypes.ADMISSION)
    const admissionFilterTag = filterTags.filter(tag => tag.id === EHealthProceduresTypes.ADMISSION)
    setFilterTags([...tagsWithoutAdmissionFilterTag, newTag])
    if (admissionFilterTag.length) {
      removeTypeFilterTag(admissionFilterTag[0])
    }
  }

  const removeAdmissionEventTag = useCallback(() => {
    setSelectedPeriod(selectedPeriod => ({
      ...createSelectedPeriodFromPrevious(selectedPeriod.previousPeriod)
    }))
  }, [])

  const removeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      setFilterTags(existingTags => existingTags.filter(ft => ft.id !== tag.id))

      switch (tag.type) {
        case 'admissionEvent':
          return removeAdmissionEventTag()
        case 'dateFilter':
          return removeDateFilterTag()
        case 'typeFilter':
          return removeTypeFilterTag(tag)
      }
    },
    [removeAdmissionEventTag, removeTypeFilterTag]
  )

  const resetFilterTags = useCallback((): void => {
    setFilterTags(defaultValue.filterTags)

    setTypeFiltersActive([])
    removeDateFilterTag()
  }, [])

  const filterHealthProceduresByTerm = useCallback(
    (term: IFilterTag) => {
      const tagByTerm = createSearchTermTagWith(term.text)
      setFilterTags(existingTags => [...existingTags, tagByTerm])
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
      changeMainContentType('timeline')
    },
    [changeMainContentType, initializeSidePanel]
  )

  return (
    <GeneralHealthProceduresContext.Provider
      value={{
        displayedHealthProcedures,
        getHealthProcedures,

        selectedPeriod,
        updateSelectedPeriod,

        mainContentType,
        changeMainContentType,

        modalHealthProcedure,
        selectMedicalEventForDetailing,
        handleFilterHealthProceduresPerDate,
        handleFilterHealthProceduresPerType,
        initializeSidePanel,
        dateFiltersActive,
        typeFiltersActive,

        errorTimeline: errorTimeline?.message ?? '',
        isLoadingTimeline,
        hasDateFiltersActive,
        hasTypeFiltersActive,

        openConductCardList,
        setOpenConductCardList,
        openOmicsCardList,
        setOpenOmicsCardList,

        showHealthProcedureDetails,
        closeHealthProcedureDetails,

        filterTags,
        setFilterTagToAdmissionEvent,
        removeFilterTag,
        resetFilterTags,

        filterHealthProceduresByTerm,

        sidePanelEvents,
        updateSidePanelEvents,
        resetSidePanelEvents
      }}
    >
      {children}
    </GeneralHealthProceduresContext.Provider>
  )
}

const useGeneralHealthProcedures = (): IGeneralHealthProceduresContextProps => {
  return React.useContext(GeneralHealthProceduresContext)
}

export { GeneralHealthProceduresContext, GeneralHealthProceduresProvider, useGeneralHealthProcedures }
